/**
 * Tiny deterministic PRNG (mulberry32). Used by AuthorIdentityService so the
 * same level seed always produces the same author, and by ShopService for
 * template selection where we want a controllable stream for the F8 overlay.
 *
 * mulberry32 is fine for cosmetic UGC — it's not cryptographic, but it has a
 * full 2^32 period and good distribution for the bit-budget we use here.
 */

export interface SeededRng {
  /** Next uniform float in [0, 1). */
  next(): number;
  /** Next int in [min, max) (max exclusive). */
  intRange(min: number, max: number): number;
  /** Pick one entry from a non-empty array. */
  pick<T>(arr: readonly T[]): T;
  /** Coin flip biased by `p` (0..1). */
  chance(p: number): boolean;
}

export function makeSeededRng(seed: number): SeededRng {
  let a = (seed | 0) || 1;
  const next = (): number => {
    a = (a + 0x6d2b79f5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  return {
    next,
    intRange(min: number, max: number): number {
      return min + Math.floor(next() * (max - min));
    },
    pick<T>(arr: readonly T[]): T {
      if (arr.length === 0) throw new Error("seededRng.pick: empty array");
      return arr[Math.floor(next() * arr.length)]!;
    },
    chance(p: number): boolean {
      return next() < p;
    },
  };
}

/**
 * Stable string → numeric seed (FNV-1a 32-bit). Used so we can derive author
 * seeds from level-key strings like "main:p123" or "sublevel:forrest:7".
 */
export function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}
